import { adminApi } from '../../api/adminApi';
import { fetchAllPages } from '../../api/fetchAllPages';

// Flags admins don't manage, left out of every admin list: appMigrationInProgress is set
// by the backend's scripts/archived/set-app-migration-flag.js during a migration
const HIDDEN_FEATURE_FLAGS = ['appMigrationInProgress'];

/**
 * Admin feature flag endpoints. listAllFeatureFlags loads every flag except HIDDEN_FEATURE_FLAGS (the table searches,
 * sorts and pages them in the browser). updateFeatureFlag (PATCH /feature-flags/:id) takes
 * { id, active?, users?, brokerGroups?, ledgers? }, where each list is
 * { add?: [id], remove?: [id] }, and refreshes the list. FeatureFlagProvider keeps reading
 * Firestore directly and picks up changes made here.
 */
export const adminFeatureFlagsApi = adminApi.injectEndpoints({
	endpoints: (builder) => ({
		listAllFeatureFlags: builder.query({
			queryFn: async (arg, api, extraOptions, baseQuery) => {
				const result = await fetchAllPages(baseQuery, '/feature-flags/list');
				if (result.error) {
					return result;
				}
				return {
					data: result.data.filter((flag) => !HIDDEN_FEATURE_FLAGS.includes(flag.feature)),
				};
			},
			providesTags: (result) => [
				{ type: 'ADMIN_FEATURE_FLAG', id: 'LIST' },
				...(result ?? []).map(({ id }) => ({ type: 'ADMIN_FEATURE_FLAG', id })),
			],
			// Fill getFeatureFlag for every flag, so single-flag readers need no request
			async onQueryStarted(arg, { dispatch, queryFulfilled }) {
				try {
					const { data: flags } = await queryFulfilled;
					dispatch(
						adminFeatureFlagsApi.util.upsertQueryEntries(
							flags.map((flag) => ({
								endpointName: 'getFeatureFlag',
								arg: flag.id,
								value: flag,
							})),
						),
					);
				} catch {
					// The list query reports its own error
				}
			},
		}),
		// One flag. Normally filled in by listAllFeatureFlags; if read before the list has
		// loaded (or after an update invalidates it), it loads the list and picks the flag.
		getFeatureFlag: builder.query({
			queryFn: async (flagId, { dispatch }) => {
				const { data: flags, error } = await dispatch(
					adminFeatureFlagsApi.endpoints.listAllFeatureFlags.initiate(undefined, {
						subscribe: false,
					}),
				);
				if (error) {
					return { error };
				}
				return { data: flags.find((flag) => flag.id === flagId) ?? null };
			},
			providesTags: (result, error, flagId) => [{ type: 'ADMIN_FEATURE_FLAG', id: flagId }],
		}),
		updateFeatureFlag: builder.mutation({
			query: ({ id, ...changes }) => ({
				url: `/feature-flags/${id}`,
				method: 'PATCH',
				body: changes,
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'ADMIN_FEATURE_FLAG', id },
				{ type: 'ADMIN_FEATURE_FLAG', id: 'LIST' },
			],
		}),
	}),
});

export const {
	useListAllFeatureFlagsQuery,
	useGetFeatureFlagQuery,
	useUpdateFeatureFlagMutation,
} = adminFeatureFlagsApi;
